/**
 * UI color theme preference — Light (default) / Dark / System.
 * Phase T product: maps to html[data-theme] role slots in tokens/colors.css.
 * Storage key reuses aman_ui_theme (legacy builds previously wiped this key).
 */
import { kvGetRaw, kvSetRaw } from '../infrastructure/storage/kvAccess';

export const THEME_STORAGE_KEY = 'aman_ui_theme';

/** User-selectable modes — light first (default / pre-T identity). */
export const THEME_USER_MODES = ['light', 'dark', 'system'];

const DEFAULT_THEME = 'light';
const SYSTEM_DARK_MQ = '(prefers-color-scheme: dark)';

const listeners = new Set();
let systemMediaBound = false;

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(readThemePreference());
    } catch {
      /* ignore */
    }
  });
}

function safeMatchMedia(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return { matches: false, addEventListener: () => {}, removeEventListener: () => {} };
  }
  return window.matchMedia(query);
}

function bindSystemListener() {
  if (systemMediaBound || typeof window === 'undefined') return;
  systemMediaBound = true;
  const mq = safeMatchMedia(SYSTEM_DARK_MQ);
  const onChange = () => {
    if (readThemePreference() === 'system') {
      applyThemePreference('system');
      notify();
    }
  };
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onChange);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(onChange);
  }
}

/**
 * Resolved theme token slot: 'light' | 'dark'.
 * Nested specialization surfaces may set their own data-theme="light" lock.
 */
export function resolveEffectiveTheme(mode = readThemePreference()) {
  if (mode === 'dark') return 'dark';
  if (mode === 'system') {
    if (typeof window === 'undefined') return 'light';
    return safeMatchMedia(SYSTEM_DARK_MQ).matches ? 'dark' : 'light';
  }
  return 'light';
}

export function readThemePreference() {
  try {
    const stored = kvGetRaw(THEME_STORAGE_KEY);
    if (THEME_USER_MODES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (THEME_USER_MODES.includes(raw)) return raw;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_THEME;
}

export function writeThemePreference(mode) {
  const next = THEME_USER_MODES.includes(mode) ? mode : DEFAULT_THEME;
  try {
    kvSetRaw(THEME_STORAGE_KEY, next);
  } catch {
    /* ignore quota */
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    }
  } catch {
    /* ignore */
  }
  applyThemePreference(next);
  notify();
  return next;
}

function syncThemeColorMeta(resolved) {
  if (typeof document === 'undefined') return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  meta.setAttribute('content', resolved === 'dark' ? '#0a0b0f' : '#2563eb');
}

/**
 * Apply theme to documentElement.
 * Light leaves data-theme unset so :root defaults match pre-T light identity.
 */
export function applyThemePreference(mode = readThemePreference()) {
  if (typeof document === 'undefined') return mode;
  const pref = THEME_USER_MODES.includes(mode) ? mode : DEFAULT_THEME;
  const resolved = resolveEffectiveTheme(pref);

  if (resolved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }

  syncThemeColorMeta(resolved);

  if (pref === 'system') {
    bindSystemListener();
  }

  return pref;
}

export function subscribeThemePreference(listener) {
  listeners.add(listener);
  if (readThemePreference() === 'system') {
    bindSystemListener();
  }
  return () => listeners.delete(listener);
}

export function initThemePreference() {
  applyThemePreference(readThemePreference());
}
